import { type CanvasEventTarget } from './event-targets';
import { isEditorContent, isEditorContainer } from './target-editor';
import { isEditorPopup } from './target-editor-popup';
import { isEditorTypeAhead } from './target-editor-typeahead';
import { isEmojiPickerContainer } from './target-emoji-picker-container';
import { isModal } from './target-modal';
import { getCanvasTargetFromDOM } from './util';

/**
 * Specific DOM target predicates, run in the order they are declared.
 */
const targets: Map<CanvasEventTarget, (target: EventTarget | null) => boolean> = new Map([
  ['modal', isModal],
  ['editor-container', isEditorContainer],
  ['editor-content', isEditorContent],
  ['editor-typeahead', isEditorTypeAhead],
  ['emoji-picker-container', isEmojiPickerContainer],
  ['editor-popup', isEditorPopup],
]);

/**
 * Computes the canvas target for a given DOM target.
 * @summary A set of predicates are used first from the above list of @see {targets}. If they fail, the DOM element's ancestry is traversed for the first element with `[data-canvas-target]`. We fallback to `none` if neither succeed.
 * @example `editor-container` resulting from the `isEditorContent` predicate.
 * @example `floating-toolbar` resulting from the DOM target having `data-canvas-target="floating-toolbar"` set in its ancestry.
 */
export const getInputTarget = (target: EventTarget | null): CanvasEventTarget => {
  if (!target) {
    return 'none';
  }

  for (const [canvasTarget, predicate] of targets.entries()) {
    if (predicate(target)) {
      return canvasTarget;
    }
  }

  const canvasTargetFromDOM = getCanvasTargetFromDOM(target);

  if (canvasTargetFromDOM) {
    return canvasTargetFromDOM;
  }

  return 'none';
};
